/**
 * O que a pessoa lê quando a API recusa.
 *
 * ── O defeito que isto conserta ───────────────────────────────────────────
 *
 * As telas mostravam `erro.message`. Num `ErroDaApi`, `message` é
 * `"CODIGO: título do catálogo"` — "REQUISICAO_INVALIDA: Requisição inválida".
 * O texto útil está em `problema.detail` e em `problema.errors`, e estava sendo
 * descartado em quinze lugares.
 *
 * O efeito é o pior tipo de silêncio: o servidor explica com cuidado — "esta
 * etapa tem 3 negócios dentro", "já existe um cliente com este telefone:
 * Marcos Silva" — e a tela responde com uma frase que não ajuda ninguém. Quem
 * lê conclui que o produto quebrou, e tenta de novo para sempre uma coisa que
 * não se resolve tentando.
 *
 * ── A ordem é a da utilidade ──────────────────────────────────────────────
 *
 * O detalhe do servidor primeiro, porque é o mais específico. Depois os campos
 * de uma validação, porque num formulário de cinco caixas "requisição
 * inválida" manda adivinhar qual. Depois a frase que a tela sabe dar para
 * aquele código. E só então a genérica, que é a única que não ajuda.
 *
 * ── Por que a tela precisa de frases por código ───────────────────────────
 *
 * Porque a API não deixa sair o `detail` de todos os códigos, e está certa:
 * `ENGINE_INDISPONIVEL` carrega mensagem de engine, `NAO_ENCONTRADO` diria o
 * que existe, e parte dos detalhes de `SEM_PERMISSAO` revela existência. Para
 * esses, quem tem de escrever é quem conhece a tela.
 */

import { ErroDaApi, ErroDeRede } from '@cliqcrm/contracts';

export const RECADO_GENERICO =
  'Não foi possível completar. Tente de novo; se continuar, avise o suporte.';

const SEM_REDE = 'Não foi possível falar com o servidor. Confira a conexão e tente de novo.';

export interface OpcoesDoRecado {
  /** A frase de fechamento, quando nada mais explica. */
  readonly generica?: string;
  /** A frase desta tela para os códigos cujo detalhe a API não deixa sair. */
  readonly porCodigo?: Readonly<Record<string, string>>;
  /**
   * Junta o código de atendimento à frase de fechamento de uma falha do
   * servidor (5xx). É ele que leva o suporte ao motivo no log quando a API não
   * deixa o motivo sair — o caso de conectar um número (#1093).
   */
  readonly comCodigoDeAtendimento?: boolean;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * O `correlation_id` inteiro, que é o que acha a linha no log.
 *
 * Só quando é um UUID: sem `crypto.randomUUID` (http fora de localhost), o
 * cliente gera um id de reserva que começa igual em toda falha, e mostrá-lo
 * mandaria o suporte atrás de um código que não distingue nada.
 */
function codigoDeAtendimento(erro: ErroDaApi): string | null {
  if (erro.problema.status < 500 || !UUID.test(erro.correlationId)) return null;
  return erro.correlationId;
}

export function recadoDeErro(erro: unknown, opcoes: OpcoesDoRecado = {}): string {
  const generica = opcoes.generica ?? RECADO_GENERICO;

  /*
   * Rede é outro problema, com outra saída. Cair na genérica mandaria a pessoa
   * "avisar o suporte" quando o que houve foi o wi-fi.
   */
  if (erro instanceof ErroDeRede) return SEM_REDE;

  if (!(erro instanceof ErroDaApi)) {
    // Erro nosso, de dentro da tela. A mensagem dele é escrita por nós e vale
    // mais que a genérica — um `Error('Sem sessão.')` diz o que houve.
    return erro instanceof Error && erro.message !== '' ? erro.message : generica;
  }

  const campos = (erro.problema.errors ?? [])
    .map((falha) => `${falha.campo}: ${falha.problema}`)
    .join('; ');

  if (erro.problema.detail) {
    return campos ? `${erro.problema.detail} (${campos})` : erro.problema.detail;
  }
  if (campos) return campos;
  const fecho = opcoes.porCodigo?.[erro.codigo] ?? generica;
  const codigo = opcoes.comCodigoDeAtendimento ? codigoDeAtendimento(erro) : null;
  return codigo ? `${fecho} Se continuar, avise o suporte com o código ${codigo}.` : fecho;
}
